/**
 * Markup rendering and coordinate scaling utilities
 * Feature: 049-critique-photo-markup
 *
 * Converts percentage-based coordinates (0-100) to pixel coordinates
 * for rendering on actual photo dimensions (resolution-independent)
 */

import type {
  CircleShape,
  RectangleShape,
  ArrowShape,
} from '@/types/markup.types';
import logger from '@/utils/logger';

/**
 * Pixel coordinates for circle rendering
 */
export interface CirclePixelCoords {
  cx: number; // Center X in pixels
  cy: number; // Center Y in pixels
  r: number; // Radius in pixels
}

/**
 * Pixel coordinates for rectangle rendering
 */
export interface RectanglePixelCoords {
  x: number; // Top-left X in pixels
  y: number; // Top-left Y in pixels
  width: number; // Width in pixels
  height: number; // Height in pixels
}

/**
 * Pixel coordinates for arrow rendering
 */
export interface ArrowPixelCoords {
  x1: number; // Start X in pixels
  y1: number; // Start Y in pixels
  x2: number; // End X in pixels
  y2: number; // End Y in pixels
}

/**
 * Convert percentage-based circle coordinates to pixels
 */
export function scaleCircleToPixels(
  shape: CircleShape,
  imageWidth: number,
  imageHeight: number
): CirclePixelCoords {
  const cx = (shape.geometry.cx / 100) * imageWidth;
  const cy = (shape.geometry.cy / 100) * imageHeight;

  // Radius scales by minimum dimension (preserves proportions)
  const minDimension = Math.min(imageWidth, imageHeight);
  const r = (shape.geometry.r / 100) * minDimension;

  return { cx, cy, r };
}

/**
 * Convert percentage-based rectangle coordinates to pixels
 */
export function scaleRectangleToPixels(
  shape: RectangleShape,
  imageWidth: number,
  imageHeight: number
): RectanglePixelCoords {
  const x = (shape.geometry.x / 100) * imageWidth;
  const y = (shape.geometry.y / 100) * imageHeight;
  const width = (shape.geometry.width / 100) * imageWidth;
  const height = (shape.geometry.height / 100) * imageHeight;

  return { x, y, width, height };
}

/**
 * Convert percentage-based arrow coordinates to pixels
 */
export function scaleArrowToPixels(
  shape: ArrowShape,
  imageWidth: number,
  imageHeight: number
): ArrowPixelCoords {
  const x1 = (shape.geometry.x1 / 100) * imageWidth;
  const y1 = (shape.geometry.y1 / 100) * imageHeight;
  const x2 = (shape.geometry.x2 / 100) * imageWidth;
  const y2 = (shape.geometry.y2 / 100) * imageHeight;

  return { x1, y1, x2, y2 };
}

/**
 * Convert pixel coordinates to percentage (for drawing tools)
 *
 * BUG FIX (#480): Changed to use image-relative coordinates (0-100 for both X and Y)
 * instead of viewBox-relative coordinates. This ensures AI-generated shapes and
 * hand-drawn shapes use the same coordinate system.
 *
 * @param photoAspectRatio - Photo width/height ratio (no longer used, kept for compatibility)
 */
export function pixelToPercentage(
  pixelX: number,
  pixelY: number,
  imageWidth: number,
  imageHeight: number,
  _photoAspectRatio: number = 1 // Kept for backwards compatibility, no longer used
): { x: number; y: number } {
  // Convert to image-relative percentages (0-100 for both X and Y)
  const x = (pixelX / imageWidth) * 100;
  const y = (pixelY / imageHeight) * 100;

  // Clamp to valid range (0-100 for both axes)
  return {
    x: Math.max(0, Math.min(100, x)),
    y: Math.max(0, Math.min(100, y)),
  };
}

/**
 * Transform image-relative coordinates (0-100, 0-100) to viewBox coordinates
 *
 * BUG FIX (#480): Shapes are now stored with image-relative Y coordinates (0-100),
 * but the SVG viewBox uses aspect-ratio-adjusted height. This function transforms
 * Y coordinates from image space to viewBox space for correct rendering.
 *
 * @param imageY - Y coordinate in image space (0-100)
 * @param photoAspectRatio - Photo width/height ratio
 * @returns Y coordinate in viewBox space (0 to 100/aspectRatio)
 */
export function imageYToViewBoxY(imageY: number, photoAspectRatio: number): number {
  const viewBoxHeight = 100 / photoAspectRatio;
  return (imageY / 100) * viewBoxHeight;
}

/**
 * Transform viewBox coordinates back to image-relative coordinates
 *
 * Used when reading coordinates from rendered shapes (e.g., during drag operations)
 *
 * @param viewBoxY - Y coordinate in viewBox space
 * @param photoAspectRatio - Photo width/height ratio
 * @returns Y coordinate in image space (0-100)
 */
export function viewBoxYToImageY(viewBoxY: number, photoAspectRatio: number): number {
  const viewBoxHeight = 100 / photoAspectRatio;
  return (viewBoxY / viewBoxHeight) * 100;
}

/**
 * Transform a shape's Y coordinates from image space (0-100) to viewBox space
 *
 * BUG FIX (#480): Creates a copy of the shape with Y coordinates transformed
 * for rendering in the aspect-ratio-adjusted SVG viewBox.
 *
 * @param shape - Shape with image-relative coordinates (Y: 0-100)
 * @param photoAspectRatio - Photo width/height ratio
 * @returns Shape with viewBox-relative coordinates (Y: 0 to 100/aspectRatio)
 */
export function transformShapeForViewBox(shape: any, photoAspectRatio: number): any {
  const transformed = { ...shape };

  // Transform geometry based on shape type
  if (shape.geometry) {
    transformed.geometry = { ...shape.geometry };

    // Transform Y coordinates (and height for rectangles/spotlights)
    if ('y' in shape.geometry) {
      transformed.geometry.y = imageYToViewBoxY(shape.geometry.y, photoAspectRatio);
    }
    if ('cy' in shape.geometry) {
      transformed.geometry.cy = imageYToViewBoxY(shape.geometry.cy, photoAspectRatio);
    }
    if ('y1' in shape.geometry) {
      transformed.geometry.y1 = imageYToViewBoxY(shape.geometry.y1, photoAspectRatio);
    }
    if ('y2' in shape.geometry) {
      transformed.geometry.y2 = imageYToViewBoxY(shape.geometry.y2, photoAspectRatio);
    }

    // Transform height (scale it to viewBox height)
    if ('height' in shape.geometry) {
      const viewBoxHeight = 100 / photoAspectRatio;
      transformed.geometry.height = (shape.geometry.height / 100) * viewBoxHeight;
    }

    // Transform radius for circles (scale to viewBox dimensions)
    if ('r' in shape.geometry) {
      const viewBoxHeight = 100 / photoAspectRatio;
      transformed.geometry.r = (shape.geometry.r / 100) * viewBoxHeight;
    }
  }

  return transformed;
}

/**
 * Calculate arrow head geometry for rendering
 * Returns triangle points for arrow head polygon
 */
export function calculateArrowHead(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  headSize: number = 10
): { p1: { x: number; y: number }; p2: { x: number; y: number }; p3: { x: number; y: number } } {
  // Calculate arrow angle
  const angle = Math.atan2(y2 - y1, x2 - x1);

  // Arrow head angle (30 degrees)
  const headAngle = Math.PI / 6;

  // Calculate the two base points of the arrow head
  const p1x = x2 - headSize * Math.cos(angle - headAngle);
  const p1y = y2 - headSize * Math.sin(angle - headAngle);

  const p2x = x2 - headSize * Math.cos(angle + headAngle);
  const p2y = y2 - headSize * Math.sin(angle + headAngle);

  return {
    p1: { x: p1x, y: p1y },
    p2: { x: x2, y: y2 }, // Arrow tip
    p3: { x: p2x, y: p2y },
  };
}

/**
 * Generate SVG path for arrow with head
 */
export function generateArrowPath(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  headSize: number = 10
): string {
  const head = calculateArrowHead(x1, y1, x2, y2, headSize);

  // Line from tail to head
  const linePath = `M ${x1} ${y1} L ${x2} ${y2}`;

  // Arrow head triangle
  const headPath = `M ${head.p1.x} ${head.p1.y} L ${head.p2.x} ${head.p2.y} L ${head.p3.x} ${head.p3.y} Z`;

  return `${linePath} ${headPath}`;
}

/**
 * Debug utility: Log shape coordinates (percentage vs pixels)
 * Use to verify coordinate scaling during development
 */
export function debugShapeCoordinates(
  shape: CircleShape | RectangleShape | ArrowShape,
  imageWidth: number,
  imageHeight: number
): void {
  const header = `📐 Shape Coordinates: ${shape.type} #${shape.number}`;
  const dimensions = `📏 Image: ${imageWidth}×${imageHeight}px`;

  if (shape.type === 'circle') {
    const pixels = scaleCircleToPixels(shape, imageWidth, imageHeight);
    logger.debug('Markup', header, dimensions,
      `🔵 Circle: center(${shape.geometry.cx}%,${shape.geometry.cy}%) → (${pixels.cx.toFixed(1)}px,${pixels.cy.toFixed(1)}px)`,
      `radius: ${shape.geometry.r}% → ${pixels.r.toFixed(1)}px`);
  } else if (shape.type === 'rectangle') {
    const pixels = scaleRectangleToPixels(shape, imageWidth, imageHeight);
    logger.debug('Markup', header, dimensions,
      `🟦 Rectangle: pos(${shape.geometry.x}%,${shape.geometry.y}%) → (${pixels.x.toFixed(1)}px,${pixels.y.toFixed(1)}px)`,
      `size: ${shape.geometry.width}%×${shape.geometry.height}% → ${pixels.width.toFixed(1)}×${pixels.height.toFixed(1)}px`);
  } else if (shape.type === 'arrow') {
    const pixels = scaleArrowToPixels(shape, imageWidth, imageHeight);
    const length = Math.sqrt(
      Math.pow(pixels.x2 - pixels.x1, 2) + Math.pow(pixels.y2 - pixels.y1, 2)
    );
    logger.debug('Markup', header, dimensions,
      `➡️ Arrow: start(${shape.geometry.x1}%,${shape.geometry.y1}%) → (${pixels.x1.toFixed(1)}px,${pixels.y1.toFixed(1)}px)`,
      `end(${shape.geometry.x2}%,${shape.geometry.y2}%) → (${pixels.x2.toFixed(1)}px,${pixels.y2.toFixed(1)}px)`,
      `length: ${length.toFixed(1)}px`);
  }
}
